import { Avatar, Button, Popover, Divider } from 'antd';
import { BellOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCachedCurrentUser } from '../helpers/storeHelper';
import './styles/userSessionMenu.scss';
import { logoutRequestApi, LogoutResponse } from '../../api/session';
import {
  showGenericOperationFailedMessage,
  userFullName,
  userInitials,
} from '../../utils/helpers';
import { queryClient } from '../../queryClient';
import {
  areNotificationsSupported,
  getNotificationPermission,
  requestNotificationPermission,
} from '../../serviceWorkerRegisterer';

interface UserSessionMenuState {
  popoverVisible: boolean;
  logoutButtonLoading: boolean;
  notificationPermission: NotificationPermission | 'unsupported';
  notificationButtonLoading: boolean;
}

const UserSessionMenu = () => {
  const [state, setState] = useState<UserSessionMenuState>({
    popoverVisible: false,
    logoutButtonLoading: false,
    notificationPermission: getNotificationPermission(),
    notificationButtonLoading: false,
  });

  // Update permission state when popover opens (in case user changed it in browser settings)
  useEffect(() => {
    if (state.popoverVisible) {
      setState((s) => ({ ...s, notificationPermission: getNotificationPermission() }));
    }
  }, [state.popoverVisible]);

  const { t, i18n } = useTranslation();
  const currentUser = getCachedCurrentUser();

  // const [logoutFn, { client }] = useMutation<GqlLogout>(GQL_LOGOUT);

  const onPopoverVisibleChange = (newValue: boolean) => {
    setState((state) => ({ ...state, popoverVisible: newValue }));
  };

  const setLogoutButtonLoading = (loading: boolean) => {
    setState((state) => ({
      ...state,
      logoutButtonLoading: loading,
    }));
  };

  const handleEnableNotifications = async () => {
    setState((s) => ({ ...s, notificationButtonLoading: true }));
    const success = await requestNotificationPermission();
    // Get actual permission status (might be granted even if subscription failed)
    const actualPermission = getNotificationPermission();
    setState((s) => ({
      ...s,
      notificationButtonLoading: false,
      notificationPermission: actualPermission,
    }));
    // Show error if permission granted but subscription failed
    if (!success && actualPermission === 'granted') {
      showGenericOperationFailedMessage(null, t);
    }
  };

  const renderNotificationButton = () => {
    const { notificationPermission, notificationButtonLoading } = state;

    if (notificationPermission === 'unsupported' || !areNotificationsSupported()) {
      return (
        <Button disabled icon={<StopOutlined />}>
          {t('generic.links.notificationsUnsupported')}
        </Button>
      );
    }

    if (notificationPermission === 'granted') {
      return (
        <Button
          icon={<CheckCircleOutlined />}
          style={{ color: 'green' }}
          loading={notificationButtonLoading}
          onClick={handleEnableNotifications}
        >
          {t('generic.links.notificationsEnabled')}
        </Button>
      );
    }

    if (notificationPermission === 'denied') {
      return (
        <Button disabled icon={<StopOutlined />}>
          {t('generic.links.notificationsBlocked')}
        </Button>
      );
    }

    // permission === 'default' - user hasn't decided yet
    return (
      <Button
        loading={notificationButtonLoading}
        onClick={handleEnableNotifications}
        icon={<BellOutlined />}
      >
        {t('generic.links.enableNotifications')}
      </Button>
    );
  };

  const logout = async () => {
    console.log('logged out');

    setLogoutButtonLoading(true);

    let logoutResponse: LogoutResponse;
    // logout backend
    try {
      logoutResponse = await logoutRequestApi();
      if (!logoutResponse.signed_out) {
        showGenericOperationFailedMessage(null, t);
        setLogoutButtonLoading(false);
        return;
      }
    } catch (e) {
      showGenericOperationFailedMessage(e, t);
      setLogoutButtonLoading(false);
      return;
    }

    // clear frontend store
    await queryClient.clear();

    // logout from oauth provider
    window.location.href = logoutResponse.redirect_url;
  };

  const popoverContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {renderNotificationButton()}
      <Divider style={{ margin: '4px 0' }} />
      <Button
        loading={state.logoutButtonLoading}
        onClick={logout}
        type={'primary'}
      >
        {t('generic.links.logout')}
      </Button>
    </div>
  );

  const localeDirection = i18n.dir();
  const popoverPlacement =
    localeDirection === 'rtl' ? 'bottomLeft' : 'bottomRight';

  return (
    <Popover
      content={popoverContent}
      title={`${t('generic.messages.welcome')} ${userFullName(currentUser)}`}
      trigger="click"
      placement={popoverPlacement}
      open={state.popoverVisible}
      onOpenChange={onPopoverVisibleChange}
      className="user-session-menu"
    >
      <Avatar className="user-session-menu__user-icon">
        {userInitials(currentUser) || 'U'}
      </Avatar>
    </Popover>
  );
};

export default UserSessionMenu;
